import React, { useState, useRef } from "react";
import { useStateValue } from "../GlobalState/StateProvider";
import { Card } from "primereact/card";
import { Toast } from "primereact/toast";
import axios from "axios";

const genderOptions = [
    { label: "Select Gender", value: "" },
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" }
];

const PatientInfo = ({ initialData, refresh }) => {
    const toast = useRef(null);

    // Format the DOB from initialData if it exists to MM-DD-YYYY format
    const formattedInitialData = { ...initialData };
    if (formattedInitialData.dob) {
        try {
            const dateObj = new Date(formattedInitialData.dob);
            if (!isNaN(dateObj.getTime())) {
                const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                const day = String(dateObj.getDate()).padStart(2, '0');
                const year = dateObj.getFullYear();
                formattedInitialData.dob = `${month}-${day}-${year}`;
            }
        } catch (e) {
            console.error("Error formatting date:", e);
        }
    }

    const [formData, setFormData] = useState(formattedInitialData);
    const [errors, setErrors] = useState({});
    const [{ user, stateList }] = useStateValue();

    const validateForm = () => {
        let newErrors = {};

        if (!formData.first_name) newErrors.first_name = "First name is required";
        if (!formData.last_name) newErrors.last_name = "Last name is required";
        if (!formData.gender) newErrors.gender = "Gender is required";
        if (!formData.email) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email format";

        if (!formData.dob) newErrors.dob = "Date of Birth is required";
        else if (!/^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/.test(formData.dob))
            newErrors.dob = "Format: MM-DD-YYYY";

        if (!formData.phone) newErrors.phone = "Phone is required";
        else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, "")))
            newErrors.phone = "Phone number must be 10 digits";

        if (!formData.address_1) newErrors.address_1 = "Address 1 is required";
        if (!formData.patient_city) newErrors.patient_city = "City is required";
        if (!formData.patient_state_id) newErrors.patient_state_id = "State is required";
        if (!formData.patient_zip) newErrors.patient_zip = "Zip code is required";
        else if (!/^\d{5}(-\d{4})?$/.test(formData.patient_zip)) newErrors.patient_zip = "Invalid zip code format";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'dob') {
            const correctedValue = value.replace(/\//g, "-");
            setFormData({ ...formData, [name]: correctedValue });
            return;
        }

        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            let submitData = { ...formData };
            if (submitData.dob && /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])-\d{4}$/.test(submitData.dob)) {
                const [month, day, year] = submitData.dob.split('-');
                submitData.dob = `${year}-${month}-${day}`;
            }

            const url = "/api/ha/SaveChanges";
            const params = {
                actionType: 'UpdatePatientInfo',
                casetype: 'case',
                caseID: initialData.id,
                refillID: initialData.refillID,
                first_name: formData.first_name,
                middle_initial: formData.middle_initial,
                last_name: formData.last_name,
                dob: submitData.dob,
                email: formData.email,
                phone: formData.phone,
                address_1: formData.address_1,
                address_2: formData.address_2,
                patient_state_id: formData.patient_state_id,
                patient_city: formData.patient_city,
                patient_zip: formData.patient_zip
            };

            axios.post(url, params)
                .then((response) => {
                    if (response.data.status === 'OK') {
                        refresh();
                        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Saved successfully!', life: 3000 });
                    } else {
                        toast.current.show({ severity: 'error', summary: 'Error', detail: response.data.errMsg, life: 5000 });
                    }
                })
                .catch((error) => {
                    const errorMsg = error.response?.data?.errMsg || error.message;
                    toast.current.show({ severity: 'error', summary: 'Error', detail: "Error: " + errorMsg, life: 5000 });
                });
        }
    };

    return (
        <div className="mt-4">
            <Toast ref={toast} />
            <Card>
                <h2 className="text-left">Patient & Subscription Info</h2>
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="d-flex flex-row mb-3">
                            <div className="p-1">
                                <label className="form-label">First Name</label>
                                <input type="text" className="form-control" name="first_name" value={formData.first_name} onChange={handleChange} />
                                {errors.first_name && <small className="text-danger">{errors.first_name}</small>}
                            </div>
                            <div className="p-1">
                                <label className="form-label">Middle Initial</label>
                                <input type="text" className="form-control" name="middle_initial" value={formData.middle_initial} onChange={handleChange} />
                            </div>
                            <div className="p-1">
                                <label className="form-label">Last Name</label>
                                <input type="text" className="form-control" name="last_name" value={formData.last_name} onChange={handleChange} />
                                {errors.last_name && <small className="text-danger">{errors.last_name}</small>}
                            </div>
                        </div>

                        <div className="d-flex flex-row mb-3">
                            <div className="p-1">
                                <label className="form-label">Gender</label>
                                <select className="form-control" value={formData.gender} onChange={handleChange} name="gender">
                                    {genderOptions.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                {errors.gender && <small className="text-danger">{errors.gender}</small>}
                            </div>
                            <div className="p-1">
                                <label className="form-label">Email</label>
                                <input type="email" className="form-control" name="email" value={formData.email} onChange={handleChange} />
                                {errors.email && <small className="text-danger">{errors.email}</small>}
                            </div>
                            <div className="p-1">
                                <label className="form-label">Date of Birth</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="dob"
                                    value={formData.dob}
                                    onChange={handleChange}
                                    placeholder="MM-DD-YYYY"
                                />
                                {errors.dob && <small className="text-danger">{errors.dob}</small>}
                            </div>
                            <div className="p-1">
                                <label className="form-label">Phone</label>
                                <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleChange} />
                                {errors.phone && <small className="text-danger">{errors.phone}</small>}
                            </div>
                        </div>

                        <div className="d-flex flex-row mb-3">
                            <div className="p-1">
                                <label className="form-label">Address</label>
                                <input type="text" className="form-control" name="address_1" value={formData.address_1} onChange={handleChange} />
                                {errors.address_1 && <small className="text-danger">{errors.address_1}</small>}
                            </div>
                            <div className="p-1">
                                <label className="form-label">Address 2</label>
                                <input type="text" className="form-control" name="address_2" value={formData.address_2} onChange={handleChange} />
                            </div>
                        </div>

                        <div className="d-flex flex-row mb-3">
                            <div className="p-1">
                                <label className="form-label">City</label>
                                <input type="text" className="form-control" name="patient_city" value={formData.patient_city} onChange={handleChange} />
                                {errors.patient_city && <small className="text-danger">{errors.patient_city}</small>}
                            </div>
                            <div className="p-1">
                                <label className="form-label">State</label>
                                <select className="form-control" value={formData.patient_state_id} onChange={handleChange} name="patient_state_id">
                                    {stateList.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                {errors.patient_state_id && <small className="text-danger">{errors.patient_state_id}</small>}
                            </div>
                            <div className="p-1">
                                <label className="form-label">Zip Code</label>
                                <input type="text" className="form-control" name="patient_zip" value={formData.patient_zip} onChange={handleChange} />
                                {errors.patient_zip && <small className="text-danger">{errors.patient_zip}</small>}
                            </div>
                        </div>

                        <div>
                            <button type="submit" className="btn btn-primary mt-3">Save Changes</button>
                        </div>
                    </div>
                </form>
            </Card>
        </div>
    );
};

export default PatientInfo;
